/**
 * What "changed from default" means in the settings editor (R9): a knob is changed when it differs from what a
 * one-click Start would run on that game — the box's defaults, what the sport adds (MLB's half fee, hockey's no
 * completion in overtime), then the trader's saved default for the sport, else the all-sports one. A saved default
 * itself is compared with the box's defaults plus what its sport adds. Pure: the editor hands it the /profile read.
 */
import type { PenelopeConfig } from '@/hooks/usePenelope';
import type { PenelopeProfileRead, PenelopeProfileSport } from '@/hooks/usePenelopeProfile';
import type { PenelopeSport } from '@/hooks/usePenelopeSides';

/** Fields with their own control (arm, mode, lean, the side switches, variants): never a "changed" knob. */
export const NOT_A_KNOB: ReadonlySet<string> = new Set(['enabled', 'mode', 'lean', 'armA', 'armB', 'variants']);

/** What one-click Start would run on a game of `sport` (null = no sport known: the all-sports default). Null until the
 *  read has the box's defaults. */
export function knobBaseline(p: PenelopeProfileRead | null | undefined, sport: PenelopeSport | null): Partial<PenelopeConfig> | null {
  if (!p?.defaults) return null;
  const adds = sport ? (p.sportDefaults?.[sport] ?? {}) : {};
  const saved = (sport ? p.profiles?.[sport] : undefined) ?? p.profiles?.all ?? null;
  return { ...p.defaults, ...adds, ...(saved?.config ?? {}) };
}

/** The base a saved default for `sport` is compared with: the box's defaults and what the sport adds. */
export function profileBaseline(p: PenelopeProfileRead | null | undefined, sport: PenelopeProfileSport): Partial<PenelopeConfig> | null {
  if (!p?.defaults) return null;
  return { ...p.defaults, ...(sport !== 'all' ? (p.sportDefaults?.[sport] ?? {}) : {}) };
}

/** Is `key` set differently from the base? Unknown on either side is not a change. */
export function isChanged(cfg: Partial<PenelopeConfig>, base: Partial<PenelopeConfig> | null | undefined, key: string): boolean {
  if (!base || NOT_A_KNOB.has(key)) return false;
  const a = cfg[key];
  const b = base[key];
  return a !== undefined && b !== undefined && JSON.stringify(a) !== JSON.stringify(b);
}

/** Every knob of `keys` set differently from the base. */
export function changedKeys(cfg: Partial<PenelopeConfig>, base: Partial<PenelopeConfig> | null | undefined, keys: Iterable<string>): string[] {
  return [...keys].filter((k) => isChanged(cfg, base, k));
}
